import type { FC, ReactNode } from 'react';
import React from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { useKeyboardShortcutsState } from './useKeyboardShortcutsState';
import { getShortcutString } from './get-shortcut-string';

const KeyboardShortcutContainerStyles = css({
	display: 'flex',
	paddingBottom: token('space.025'),
	paddingTop: token('space.025'),
});

export const KeyboardShortcutContainer = ({ children }: { children?: React.ReactNode }) => (
	<div css={KeyboardShortcutContainerStyles}>{children}</div>
);

const KeyboardShortcutDefaultStyles = css({
	borderRadius: '2px',
	marginLeft: token('space.025'),
	marginRight: token('space.025'),
	padding: `1px ${token('space.100')}`,
});

const darkBackgroundStyles = css({
	backgroundColor: token('color.background.accent.gray.subtle'),
});

const lightBackgroundStyles = css({
	backgroundColor: token('color.background.accent.gray.subtlest'),
});

const KeyboardShortcutStylesWithContentBefore = css({
	marginLeft: token('space.050'),
	marginRight: token('space.0'),
});

export const KeyboardShortcut = ({
	hasContentBefore,
	children,
	isTooltip,
}: {
	hasContentBefore?: boolean;
	children?: React.ReactNode;
	isTooltip: boolean;
}) => {
	return (
		<div
			css={[
				KeyboardShortcutDefaultStyles,
				hasContentBefore && KeyboardShortcutStylesWithContentBefore,
				isTooltip ? darkBackgroundStyles : lightBackgroundStyles,
			]}
		>
			{children}
		</div>
	);
};

export type ShortcutVisualizerProps = {
	shortcut: string;
	contentBefore?: ReactNode;
	isEditorShortcut?: boolean;
	isTooltip?: boolean;
};

export const ShortcutVisualizer: FC<ShortcutVisualizerProps> = ({
	shortcut,
	contentBefore,
	isEditorShortcut,
	isTooltip = true,
}) => {
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsState();
	const shortcutString = getShortcutString(shortcut);

	return (
		<KeyboardShortcutContainer>
			{contentBefore}
			{(areKeyboardShortcutsEnabled || isEditorShortcut) &&
				shortcutString.split(' ').map((shortcutSegment, index) => (
					<KeyboardShortcut
						key={`${shortcutSegment}-${index}`}
						hasContentBefore={!!contentBefore}
						isTooltip={isTooltip}
					>
						{shortcutSegment}
					</KeyboardShortcut>
				))}
		</KeyboardShortcutContainer>
	);
};
